/**
 * Static content tables for the homepage. Extracted from Home.jsx so the
 * page component stays focused on layout/wiring while copy/images can be
 * edited (and code-reviewed) in isolation.
 *
 * Anything that imports React/JSX or lucide icons lives here too because
 * HERO_SLIDES has JSX in its `titleNode` and CATEGORIES + WHY hold icon
 * components.
 */
import {
  Flame, Droplets, ChefHat, Sprout, Leaf, Gift,
  Award, Sparkles, ShieldCheck, Heart,
  Wheat,
} from "lucide-react";

// HERO SLIDE 1 — full product range showcase image (user-supplied iter27).
export const HERO_ALL_PRODUCTS_IMG =
  "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/ibjx47om_1000651844.png";

// CATEGORY IMAGES — direct product photos from Shopify CDN.
const IMG_PULIHORA = "https://cdn.shopify.com/s/files/1/0749/4212/0122/files/D2.png?v=1776680770";
const IMG_PAPPULA = "https://cdn.shopify.com/s/files/1/0749/4212/0122/files/Pappula_Podi.png?v=1780896945";
const IMG_RAGI = "https://cdn.shopify.com/s/files/1/0749/4212/0122/files/Ragi_Flour_23bbf842-a9e1-457d-993e-f026672eebc1.png?v=1780903210";
// Wood Pressed Oils — Coconut + Groundnut bottles together (user-supplied iter27).
const IMG_OILS_DUO =
  "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/u7phuvcz_1000650040.png";

// Story illustration of ammamma — re-used in the redesigned Our Story.
// iter87 — swapped to the newly-uploaded, 117 KB JPG (previous 2.4 MB PNG
// caused unreliable renders on slow / cached client networks even after
// preload+eager fixes).  Hosted on the newer `.net` Emergent CDN which
// is the stable, forward-facing endpoint (the older `.com` host is being
// gradually retired).
export const STORY_PORTRAIT_IMG =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_spice-bazaar-124/artifacts/j7wea2i1_1000699606.jpg";
// Legacy URL kept for the runtime fallback chain (if the primary CDN ever
// hiccups, the component retries with this before showing the SVG).
export const STORY_PORTRAIT_IMG_FALLBACK =
  "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/achkm72v_ChatGPT_Image_May_8_2026_06_15_03_PM.png";

export const HERO_SLIDES = [
  {
    id: "all-products",
    img: HERO_ALL_PRODUCTS_IMG,
    alt: "Sri Durga Foods — pickles, masalas, spices, flours, podis and wood-pressed oils",
    titleNode: (
      <>
        <span className="text-white">Rooted in </span>
        <span className="text-brand-turmeric">Andhra</span>
        <span className="text-white">,</span>
        <br />
        <span className="text-white">Crafted for </span>
        <span className="text-brand-turmeric">Every Home</span>
      </>
    ),
    subtitle:
      "Hand-cut, sun-cured and slow-cooked pickles, culinary pastes, stoneground spices, fragrant masalas, traditional powders, flours and wood-pressed oils — made the way Andhra families have always made them.",
    ctaLabel: "Shop the collection",
    ctaHref: "/shop",
  },
  {
    id: "pickles",
    img: "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/wnrwd64m_1000651850.png",
    alt: "Sri Durga Pickles — Ginger, Garlic, Mango Avakaya, Amla and Gongura",
    overline: "Pickles",
    title: "Traditional Andhra Pickles.",
    subtitle: "Authentic recipes handcrafted with experience.",
    ctaLabel: "Shop pickles",
    ctaHref: "/shop?category=Pickles",
  },
  {
    id: "spices",
    img: "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/payp23fm_1000651369.png",
    alt: "Sri Durga stoneground spices — two rows of spices and masalas",
    overline: "Stoneground",
    title: "Stoneground Spices.",
    subtitle: "Freshly processed for rich aroma, colour and flavour.",
    ctaLabel: "Shop spices",
    ctaHref: "/shop?category=Spices",
  },
  {
    id: "masalas",
    img: "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/zu3qwfyo_1000651365.png",
    alt: "Sri Durga hand-roasted masalas — Chicken, Garam and Mutton Masala",
    overline: "Hand-roasted blends",
    title: "Hand-Roasted Masalas.",
    subtitle: "Perfectly balanced spice blends for every kitchen.",
    ctaLabel: "Shop masalas",
    ctaHref: "/shop?category=Masalas",
  },
  {
    id: "oils",
    img: "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/u7phuvcz_1000650040.png",
    alt: "Sri Durga wood-pressed Coconut Oil and Groundnut Oil",
    overline: "Wood pressed",
    title: "Pure Wood-Pressed Oils.",
    subtitle: "Traditional extraction methods that preserve natural nutrition and flavour.",
    ctaLabel: "Shop oils",
    ctaHref: "/shop?category=Wood Pressed Oils",
  },
];

export const CATEGORIES = [
  { name: "Pickles", icon: Flame, unit: "Varieties", img: "https://cdn.shopify.com/s/files/1/0749/4212/0122/files/Mango_Avakaya_Pickle.jpg?v=1776680268" },
  { name: "Culinary Pastes", icon: Leaf, unit: "Staples", img: IMG_PULIHORA },
  { name: "Spices", icon: Sprout, unit: "Spice Powders", img: "https://cdn.shopify.com/s/files/1/0749/4212/0122/files/Draft_3_2.png?v=1778568944" },
  { name: "Masalas", icon: ChefHat, unit: "Blends", img: "https://cdn.shopify.com/s/files/1/0749/4212/0122/files/Chicken_Masala.png?v=1776668191" },
  { name: "Traditional Powders", icon: Sprout, unit: "Podis", img: IMG_PAPPULA },
  { name: "Flours", icon: Leaf, unit: "Flours", img: IMG_RAGI },
  { name: "Wood Pressed Oils", icon: Droplets, unit: "Oils", img: IMG_OILS_DUO },
  // iter99 — "Whole Spices" is admin-managed (no bundled img), but we
  // register it here so the Home page picks up its lucide icon (Wheat)
  // via the ``bundled?.icon`` lookup in Home.jsx.  Icon style, size and
  // colour match every other tile — no card layout was changed.
  { name: "Whole Spices", icon: Wheat, unit: "Spices", img: "" },
  // iter68 — Combos is a first-class category.  The card is HIDDEN from
  // the homepage grid by Home.jsx whenever the category has no product
  // with a main image (see the `combosFirstImage` logic in Home.jsx),
  // so we intentionally leave `img: ""` here — the homepage will
  // auto-hydrate the tile with the first combo's main image the moment
  // the admin uploads one.  Admins can also override the image via the
  // usual Admin → Homepage → Shop By Category interface.
  { name: "Combos", icon: Gift, unit: "Bundles", img: "" },
];

// Bundled default Crowd Favourites — resolved against the live products list
// at render time (Home.jsx) or used as the admin pre-fill seed for the
// BestsellerCurator panel.
export const BUNDLED_BESTSELLER_NAMES = [
  "Mango Avakaya Pickle",
  "Tomato Pickle",
  "Lemon Pickle",
  "Pulihora Paste",
  "Red Chilli Powder",
  "Chicken Masala Powder",
  "Pappula Podi",
  "Idli Karam Powder",
  "Coconut Oil",
  "Groundnut Oil",
];

// Five pillars rendered in the "Why Choose Sri Durga Foods" section.
export const WHY = [
  {
    title: "25+ Years of Trusted Taste",
    desc: "Bringing authentic Andhra flavours to homes and businesses for over 25 years.",
    icon: Award,
  },
  {
    title: "Made with Rice Bran Oil",
    desc: "Our pickles and pastes are prepared using rice bran oil, never palm oil.",
    icon: Heart,
  },
  {
    title: "No Artificial Colours",
    desc: "Real ingredients. Real colour. Real flavour.",
    icon: Sparkles,
  },
  {
    title: "Hygienically Manufactured",
    desc: "Produced in an FSSAI-compliant facility with strict quality standards.",
    icon: ShieldCheck,
  },
  {
    title: "Authentic Andhra Recipes",
    desc: "Traditional recipes crafted to preserve the taste Andhra families love.",
    icon: Leaf,
  },
];

export const FAQ = [
  {
    q: "What is the shelf life of your Products?",
    a: "Pickles stay fresh for 9 months from manufacture date when stored properly. Pastes 6 months, spices 9 months, Traditional Powders 6 months, oils 6 months. Each jar has a printed best before date.",
  },
  {
    q: "How should I store the products?",
    a: "Keep in a cool, dry place away from direct sunlight. Use a clean, dry spoon every time. Refrigeration is optional but extends life. Never let water enter the jar.",
  },
  {
    q: "What ingredients do you use?",
    a: "We use carefully selected, high-quality ingredients such as fresh vegetables, spices, herbs, Rice Bran Oil, and traditional seasonings. Our ingredients are chosen to deliver authentic taste, aroma, and quality in every product. No Palm oil. No artificial colours.",
  },
  {
    q: "Can I cancel or change my order?",
    aLines: [
      { label: "Cancellation:", text: "No." },
      { label: "Order Changes:", text: "No." },
    ],
  },
  {
    q: "Do you offer bulk orders?",
    a: "Yes. WhatsApp us for custom quantities — we love big orders.",
  },
];
